import React, { useState, useEffect, useRef } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { SelectButton } from "primereact/selectbutton";
import { InputText } from "primereact/inputtext";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { Toast } from "primereact/toast";
import FinancialAccountService from "../../services/FinancialAccountService";

export default function MigrationWizardDialog() {
  const [visible, setVisible] = useState(false);
  const [accountsToMigrate, setAccountsToMigrate] = useState([]);
  const [classifications, setClassifications] = useState({}); // { accountId: 3 or 4 }
  const [migrationDetails, setMigrationDetails] = useState({}); // { accountId: { ...details } }
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showQuickForm, setShowQuickForm] = useState(null); // 'CARD' or 'POS' or null
  const toast = useRef(null);

  // Quick form states
  const [newAcc, setNewAcc] = useState({
    name: "",
    accountNumber: "",
    cardLimit: 0,
    statementDay: 1,
    paymentDueDay: 10,
    associatedBankAccountId: null,
    merchantNo: "",
    terminalNo: "",
    defaultCommissionRate: 0,
    defaultValueDays: 0,
  });

  const optionTypes = [
    { label: "💳 Firma Kartı", value: 3 },
    { label: "🎰 POS Hesabı", value: 4 }
  ];

  useEffect(() => {
    checkMigrationStatus();
  }, []);

  const [newlyCreatedAccountIds, setNewlyCreatedAccountIds] = useState([]);

  const originalCardIdsRef = useRef(null);

  const checkMigrationStatus = async (currentNewlyCreatedIds = newlyCreatedAccountIds) => {
    if (localStorage.getItem("kasambenim_cards_migrated") === "true") {
      return;
    }

    try {
      const res = await FinancialAccountService.getAll();
      if (res.success) {
        const list = res.data || [];
        const banks = list.filter(a => a.accountType === 2);
        setBankAccounts(banks);

        // Fetch type 3 accounts, plus newly created accounts from this session (which could be type 3 or type 4)
        const allCards = list.filter(a => a.accountType === 3 || currentNewlyCreatedIds.includes(a.id));

        // Store the IDs of cards needing migration on first load
        if (originalCardIdsRef.current === null) {
          originalCardIdsRef.current = allCards.map(c => c.id);
        }

        // Include cards that were present at the start of the session OR newly created in this session
        const cards = allCards.filter(c => originalCardIdsRef.current.includes(c.id) || currentNewlyCreatedIds.includes(c.id));

        if (cards.length > 0) {
          setClassifications(prevClass => {
            const nextClass = { ...prevClass };
            cards.forEach(c => {
              if (nextClass[c.id] === undefined) {
                nextClass[c.id] = c.accountType; // Default to company card (3) or the actual newly created type (3 or 4)
              }
            });
            return nextClass;
          });

          setMigrationDetails(prevDetails => {
            const nextDetails = { ...prevDetails };
            cards.forEach(c => {
              if (nextDetails[c.id] === undefined) {
                nextDetails[c.id] = {
                  associatedBankAccountId: c.associatedBankAccountId || null,
                  merchantNo: c.merchantNo || "",
                  terminalNo: c.terminalNo || "",
                  defaultCommissionRate: c.defaultCommissionRate || 0,
                  defaultValueDays: c.defaultValueDays || 0,
                  cardLimit: c.cardLimit || 0,
                  statementDay: c.statementDay || 1,
                  paymentDueDay: c.paymentDueDay || 10
                };
              }
            });
            return nextDetails;
          });

          setAccountsToMigrate(cards);
          setVisible(true);
        } else {
          localStorage.setItem("kasambenim_cards_migrated", "true");
        }
      }
    } catch (err) {
      console.error("Migration check failed:", err);
    }
  };

  const handleClassChange = (accountId, val) => {
    if (!val) return;
    setClassifications({ ...classifications, [accountId]: val });
  };

  const handleDetailChange = (accountId, field, val) => {
    setMigrationDetails(prev => ({
      ...prev,
      [accountId]: {
        ...prev[accountId],
        [field]: val
      }
    }));
  };

  const handleSaveMigration = async () => {
    setLoading(true);
    try {
      let successCount = 0;
      for (const card of accountsToMigrate) {
        // Skip newly created accounts from the update migration loop since they are already configured
        if (newlyCreatedAccountIds.includes(card.id)) {
          continue;
        }

        const targetType = classifications[card.id];
        const details = migrationDetails[card.id] || {};
        
        const updateData = {
          ...card,
          accountType: targetType,
          associatedBankAccountId: targetType === 4 ? details.associatedBankAccountId : null,
          merchantNo: targetType === 4 ? details.merchantNo : "",
          terminalNo: targetType === 4 ? details.terminalNo : "",
          defaultCommissionRate: targetType === 4 ? (details.defaultCommissionRate ?? 0) : 0,
          defaultValueDays: targetType === 4 ? (details.defaultValueDays ?? 0) : 0,
          cardLimit: targetType === 3 ? (details.cardLimit ?? 0) : 0,
          statementDay: targetType === 3 ? (details.statementDay ?? 1) : 1,
          paymentDueDay: targetType === 3 ? (details.paymentDueDay ?? 10) : 10
        };

        const res = await FinancialAccountService.update(updateData);
        if (res.success) successCount++;
      }

      toast.current?.show({
        severity: "success",
        summary: "Başarılı",
        detail: "Hesap tanımlarınız başarıyla güncellendi."
      });
      
      localStorage.setItem("kasambenim_cards_migrated", "true");
      setTimeout(() => {
        setVisible(false);
        window.location.reload();
      }, 1500);
    } catch (err) {
      toast.current?.show({
        severity: "error",
        summary: "Hata",
        detail: "Güncelleme sırasında bir hata oluştu."
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateQuickAccount = async () => {
    if (!newAcc.name) {
      toast.current?.show({ severity: "warn", summary: "Uyarı", detail: "Hesap adı zorunludur" });
      return;
    }

    setLoading(true);
    try {
      const type = showQuickForm === "CARD" ? 3 : 4;
      const accountData = {
        name: newAcc.name,
        accountType: type,
        accountNumber: newAcc.accountNumber,
        openingBalance: 0,
        currencyCode: "TRY",
        isActive: true,
        // Card specific
        cardLimit: type === 3 ? newAcc.cardLimit : 0,
        statementDay: type === 3 ? newAcc.statementDay : 1,
        paymentDueDay: type === 3 ? newAcc.paymentDueDay : 10,
        // POS specific
        associatedBankAccountId: type === 4 ? newAcc.associatedBankAccountId : null,
        merchantNo: type === 4 ? newAcc.merchantNo : "",
        terminalNo: type === 4 ? newAcc.terminalNo : "",
        defaultCommissionRate: type === 4 ? newAcc.defaultCommissionRate : 0,
        defaultValueDays: type === 4 ? newAcc.defaultValueDays : 0,
      };

      const res = await FinancialAccountService.create(accountData);
      if (res.success) {
        toast.current?.show({
          severity: "success",
          summary: "Başarılı",
          detail: "Yeni hesap başarıyla eklendi."
        });
        
        let updatedIds = newlyCreatedAccountIds;
        if (res.data) {
          updatedIds = [...newlyCreatedAccountIds, res.data];
          setNewlyCreatedAccountIds(updatedIds);
        }

        // Reset form
        setNewAcc({
          name: "",
          accountNumber: "",
          cardLimit: 0,
          statementDay: 1,
          paymentDueDay: 10,
          associatedBankAccountId: null,
          merchantNo: "",
          terminalNo: "",
          defaultCommissionRate: 0,
          defaultValueDays: 0,
        });
        setShowQuickForm(null);
        
        // Refresh check to reload migration list with newly created account
        checkMigrationStatus(updatedIds);
      } else {
        toast.current?.show({ severity: "error", summary: "Hata", detail: res.message });
      }
    } catch (err) {
      toast.current?.show({ severity: "error", summary: "Hata", detail: "İşlem başarısız oldu." });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />
      
      <Dialog
        header={
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-2xl bg-orange-100 text-orange-600 shadow-lg shadow-orange-100/30">
              <i className="pi pi-sparkles text-2xl"></i>
            </div>
            <div>
              <h3 className="text-xl font-black text-slate-800 m-0 uppercase tracking-tight">
                Finansal Güncelleme Sihirbazı
              </h3>
              <p className="text-xs text-slate-500 font-medium m-0">
                Kredi kartı ve POS hesaplarınızı düzenleyin ve hızlıca tanımlayın
              </p>
            </div>
          </div>
        }
        visible={visible}
        style={{ width: "580px" }}
        onHide={() => setVisible(false)}
        className="rounded-[2.5rem] p-fluid"
        closable={false}
        footer={
          <div className="flex justify-between items-center gap-2 w-full pt-4 border-t border-slate-100">
            <Button
              label="Daha Sonra Hatırlat"
              icon="pi pi-clock"
              className="p-button-text p-button-secondary font-bold rounded-xl"
              onClick={() => setVisible(false)}
              disabled={loading}
            />
            <Button
              label="Değişiklikleri Kaydet"
              icon="pi pi-check"
              className="!bg-emerald-600 border-none font-black text-white px-6 py-3 rounded-xl shadow-lg shadow-emerald-600/20"
              onClick={handleSaveMigration}
              loading={loading}
            />
          </div>
        }
      >
        <div className="space-y-6 pt-4 max-h-[60vh] overflow-y-auto px-1">
          <div className="bg-slate-50 border border-slate-100 rounded-3xl p-5 text-sm text-slate-600 leading-relaxed shadow-sm">
            Kasa ve banka bakiyelerinizi daha doğru takip edebilmeniz için 
            <strong className="text-slate-800 font-black"> Kredi Kartı </strong> hesaplarınızı ikiye ayırdık. 
            Aşağıdaki kayıtlı hesaplarınızın doğru türünü ve detaylarını girip kaydedebilirsiniz:
          </div>

          {/* Migration List */}
          <div className="space-y-4">
            <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Kayıtlı Kredi Kartlarınız</h4>
            {accountsToMigrate.filter(acc => !newlyCreatedAccountIds.includes(acc.id)).map(acc => {
              const currentType = classifications[acc.id];
              const details = migrationDetails[acc.id] || {};

              return (
                <div 
                  key={acc.id} 
                  className="p-4 bg-white border border-slate-200 rounded-2xl shadow-sm hover:border-slate-300 transition-all duration-300 space-y-3"
                >
                  <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
                    <div className="flex flex-col min-w-0">
                      <span className="font-black text-slate-700 text-sm truncate">{acc.name}</span>
                      <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                        {acc.accountNumber || "Hesap No Yok"} • {acc.balance?.toLocaleString()} {acc.currencyCode}
                      </span>
                    </div>
                    <SelectButton
                      value={currentType}
                      options={optionTypes}
                      onChange={(e) => handleClassChange(acc.id, e.value)}
                      className="custom-selectbutton text-xs font-bold"
                    />
                  </div>

                  {/* Inline POS Form */}
                  {currentType === 4 && (
                    <div className="bg-cyan-50/50 rounded-xl p-3.5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs animate-fade-in border border-cyan-100">
                      <div className="flex flex-col gap-1 col-span-full">
                        <label className="text-[9px] font-black text-cyan-700 uppercase tracking-wider">Aktarış Banka Hesabı</label>
                        <Dropdown
                          value={details.associatedBankAccountId}
                          options={bankAccounts}
                          optionLabel="name"
                          optionValue="id"
                          onChange={(e) => handleDetailChange(acc.id, "associatedBankAccountId", e.value)}
                          placeholder="Aktarılacağı bankayı seçin"
                          className="rounded-lg border-cyan-200 text-xs h-[32px] flex items-center bg-white"
                          appendTo="self"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[9px] font-black text-cyan-700 uppercase tracking-wider">Komisyon Oranı (%)</label>
                        <InputNumber
                          value={details.defaultCommissionRate}
                          onValueChange={(e) => handleDetailChange(acc.id, "defaultCommissionRate", e.value)}
                          min={0}
                          max={100}
                          minFractionDigits={2}
                          inputClassName="rounded-lg border-cyan-200 py-1.5 px-2 text-xs bg-white w-full font-bold"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[9px] font-black text-cyan-700 uppercase tracking-wider">Valör Günü</label>
                        <InputNumber
                          value={details.defaultValueDays}
                          onValueChange={(e) => handleDetailChange(acc.id, "defaultValueDays", e.value)}
                          min={0}
                          inputClassName="rounded-lg border-cyan-200 py-1.5 px-2 text-xs bg-white w-full font-bold"
                        />
                      </div>
                    </div>
                  )}

                  {/* Inline Card Form */}
                  {currentType === 3 && (
                    <div className="bg-orange-50/40 rounded-xl p-3.5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs animate-fade-in border border-orange-100/70">
                      <div className="flex flex-col gap-1">
                        <label className="text-[9px] font-black text-orange-700 uppercase tracking-wider">Kart Limiti</label>
                        <InputNumber
                          value={details.cardLimit}
                          onValueChange={(e) => handleDetailChange(acc.id, "cardLimit", e.value)}
                          min={0}
                          inputClassName="rounded-lg border-orange-200 py-1.5 px-2 text-xs bg-white w-full font-bold"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[9px] font-black text-orange-700 uppercase tracking-wider">Kesim Günü</label>
                        <InputNumber
                          value={details.statementDay}
                          onValueChange={(e) => handleDetailChange(acc.id, "statementDay", e.value)}
                          min={1}
                          max={31}
                          inputClassName="rounded-lg border-orange-200 py-1.5 px-2 text-xs bg-white w-full font-bold"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-[9px] font-black text-orange-700 uppercase tracking-wider">Son Ödeme Günü</label>
                        <InputNumber
                          value={details.paymentDueDay}
                          onValueChange={(e) => handleDetailChange(acc.id, "paymentDueDay", e.value)}
                          min={1}
                          max={31}
                          inputClassName="rounded-lg border-orange-200 py-1.5 px-2 text-xs bg-white w-full font-bold"
                        />
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Newly Created Accounts List */}
          {accountsToMigrate.some(acc => newlyCreatedAccountIds.includes(acc.id)) && (
            <div className="space-y-4 pt-6 border-t border-slate-100 animate-fade-in">
              <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest ml-1">Yeni Eklenen Hesaplar</h4>
              {accountsToMigrate.filter(acc => newlyCreatedAccountIds.includes(acc.id)).map(acc => {
                const currentType = classifications[acc.id];

                return (
                  <div 
                    key={acc.id} 
                    className="p-4 bg-slate-50/70 border border-slate-200 border-dashed rounded-2xl shadow-sm space-y-3 opacity-90"
                  >
                    <div className="flex flex-col sm:flex-row justify-between sm:items-center gap-3">
                      <div className="flex flex-col min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-slate-700 text-sm truncate">{acc.name}</span>
                          <span className="text-[9px] bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full font-black uppercase tracking-wider">
                            Yeni Eklendi
                          </span>
                        </div>
                        <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider mt-0.5">
                          {acc.accountNumber || "Hesap No Yok"} • {acc.balance?.toLocaleString()} {acc.currencyCode}
                        </span>
                      </div>
                      <div className="px-3 py-1.5 bg-white text-slate-600 border border-slate-200 rounded-xl text-xs font-black flex items-center gap-1.5 shadow-sm">
                        {currentType === 3 ? "💳 Firma Kartı" : "🎰 POS Hesabı"}
                      </div>
                    </div>

                    {/* Inline POS Form (Read-only) */}
                    {currentType === 4 && (
                      <div className="bg-cyan-50/30 rounded-xl p-3.5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs border border-cyan-100/50">
                        <div className="flex flex-col gap-1 col-span-full">
                          <label className="text-[9px] font-black text-cyan-600 uppercase tracking-wider">Aktarış Banka Hesabı</label>
                          <Dropdown
                            value={acc.associatedBankAccountId}
                            options={bankAccounts}
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Aktarılacağı bankayı seçin"
                            className="rounded-lg border-cyan-100 text-xs h-[32px] flex items-center bg-white/70"
                            appendTo="self"
                            disabled
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-black text-cyan-600 uppercase tracking-wider">Komisyon Oranı (%)</label>
                          <InputNumber
                            value={acc.defaultCommissionRate}
                            minFractionDigits={2}
                            inputClassName="rounded-lg border-cyan-100 py-1.5 px-2 text-xs bg-white/70 w-full font-bold"
                            disabled
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-black text-cyan-600 uppercase tracking-wider">Valör Günü</label>
                          <InputNumber
                            value={acc.defaultValueDays}
                            inputClassName="rounded-lg border-cyan-100 py-1.5 px-2 text-xs bg-white/70 w-full font-bold"
                            disabled
                          />
                        </div>
                      </div>
                    )}

                    {/* Inline Card Form (Read-only) */}
                    {currentType === 3 && (
                      <div className="bg-orange-50/20 rounded-xl p-3.5 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs border border-orange-100/40">
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-black text-orange-600 uppercase tracking-wider">Kart Limiti</label>
                          <InputNumber
                            value={acc.cardLimit}
                            inputClassName="rounded-lg border-orange-100 py-1.5 px-2 text-xs bg-white/70 w-full font-bold"
                            disabled
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-black text-orange-600 uppercase tracking-wider">Kesim Günü</label>
                          <InputNumber
                            value={acc.statementDay}
                            inputClassName="rounded-lg border-orange-100 py-1.5 px-2 text-xs bg-white/70 w-full font-bold"
                            disabled
                          />
                        </div>
                        <div className="flex flex-col gap-1">
                          <label className="text-[9px] font-black text-orange-600 uppercase tracking-wider">Son Ödeme Günü</label>
                          <InputNumber
                            value={acc.paymentDueDay}
                            inputClassName="rounded-lg border-orange-100 py-1.5 px-2 text-xs bg-white/70 w-full font-bold"
                            disabled
                          />
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Quick Account Definition Section */}
          <div className="border-t border-slate-100 pt-6">
            {!showQuickForm ? (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  label="Yeni Firma Kartı Tanımla"
                  icon="pi pi-credit-card"
                  className="p-button-outlined p-button-secondary rounded-xl font-bold py-3 text-xs flex-1"
                  onClick={() => setShowQuickForm("CARD")}
                />
                <Button
                  label="Yeni POS Hesabı Tanımla"
                  icon="pi pi-print"
                  className="p-button-outlined p-button-secondary rounded-xl font-bold py-3 text-xs flex-1"
                  onClick={() => setShowQuickForm("POS")}
                />
              </div>
            ) : (
              <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 space-y-4 animate-fade-in">
                <div className="flex justify-between items-center">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-widest">
                    {showQuickForm === "CARD" ? "HIZLI FİRMA KART TANIMI" : "HIZLI POS HESAP TANIMI"}
                  </h4>
                  <Button
                    icon="pi pi-times"
                    className="p-button-rounded p-button-text p-button-secondary p-button-sm w-8 h-8"
                    onClick={() => setShowQuickForm(null)}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5 col-span-full">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">HESAP ADI</label>
                    <InputText
                      value={newAcc.name}
                      onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })}
                      placeholder="Örn: Garanti Kartı veya Akbank POS"
                      className="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                    />
                  </div>

                  <div className="flex flex-col gap-1.5">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">HESAP / KART NO</label>
                    <InputText
                      value={newAcc.accountNumber}
                      onChange={(e) => setNewAcc({ ...newAcc, accountNumber: e.target.value })}
                      placeholder="Kart no veya IBAN"
                      className="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                    />
                  </div>

                  {showQuickForm === "CARD" ? (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">KART LİMİTİ</label>
                        <InputNumber
                          value={newAcc.cardLimit}
                          onValueChange={(e) => setNewAcc({ ...newAcc, cardLimit: e.value })}
                          min={0}
                          placeholder="0.00"
                          inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">KESİM GÜNÜ</label>
                        <InputNumber
                          value={newAcc.statementDay}
                          onValueChange={(e) => setNewAcc({ ...newAcc, statementDay: e.value })}
                          min={1}
                          max={31}
                          inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">SON ÖDEME GÜNÜ</label>
                        <InputNumber
                          value={newAcc.paymentDueDay}
                          onValueChange={(e) => setNewAcc({ ...newAcc, paymentDueDay: e.value })}
                          min={1}
                          max={31}
                          inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">AKTARIŞ BANKA HESABI</label>
                        <Dropdown
                          value={newAcc.associatedBankAccountId}
                          options={bankAccounts}
                          optionLabel="name"
                          optionValue="id"
                          onChange={(e) => setNewAcc({ ...newAcc, associatedBankAccountId: e.value })}
                          placeholder="Banka Seçiniz"
                          className="rounded-xl border-slate-200 font-bold text-sm h-[40px] flex items-center"
                          appendTo="self"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">KOMİSYON ORANI (%)</label>
                        <InputNumber
                          value={newAcc.defaultCommissionRate}
                          onValueChange={(e) => setNewAcc({ ...newAcc, defaultCommissionRate: e.value })}
                          min={0}
                          max={100}
                          minFractionDigits={2}
                          placeholder="0.00"
                          inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                      </div>
                      <div className="flex flex-col gap-1.5">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider ml-1">VALÖR GÜNÜ</label>
                        <InputNumber
                          value={newAcc.defaultValueDays}
                          onValueChange={(e) => setNewAcc({ ...newAcc, defaultValueDays: e.value })}
                          min={0}
                          placeholder="Gün sayısı"
                          inputClassName="rounded-xl border-slate-200 py-2.5 font-bold text-sm"
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    label="Vazgeç"
                    className="p-button-text p-button-secondary rounded-xl font-bold p-button-sm text-xs"
                    onClick={() => setShowQuickForm(null)}
                  />
                  <Button
                    label="Hesabı Kaydet"
                    icon="pi pi-plus"
                    className="!bg-slate-900 border-none font-black text-white rounded-xl p-button-sm px-4 text-xs"
                    onClick={handleCreateQuickAccount}
                    loading={loading}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </Dialog>
    </>
  );
}
